import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Box } from '@mui/material';

import Typography from 'src/components/ui/Typography';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import useUser from 'src/hooks/useUser';
import Divider from 'src/components/ui/Divider';
import Loader from 'src/components/ui/Loader';

import { useRecommendingUsersQuery } from 'src/hooks/__generated__/queries';
import Button from 'src/components/ui/Button';
import type { Company, User } from 'src/types/__generated__/graphql';
import { isBottomDiv } from 'src/utils/isBottom';
import useAlerts from 'src/hooks/useAlerts';
import { styledComponent } from 'src/utils/styled';

const StyledBox = styledComponent(Box)<{
    $allowFetchMore: boolean;
}>`
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    ${({ $allowFetchMore }) =>
        $allowFetchMore &&
        `
        height: 25rem;
        margin-left: -0.5rem;
        margin-right: -0.5rem;
        margin-top: 1.75rem;
        overflow: auto;
    `};
`;

interface Props {
    allowFetchMore: boolean;
    limit: number;
    onSeeAllClick?: () => void;
    company: Pick<Company, 'id' | 'name' | 'recommendationsReceivedCount'>;
}

const DirectRecommendations: React.FC<Props> = ({ allowFetchMore, company, limit, onSeeAllClick }) => {
    const { t } = useTranslation(['common', 'profile']);
    const currentUser = useUser();
    const { addAlert } = useAlerts();
    const [allDataLoaded, setAllDataLoaded] = useState(false);

    const {
        data: recommendingUsersAllData,
        loading,
        fetchMore: fetchMoreRecommendingAllUsers,
    } = useRecommendingUsersQuery({
        variables: {
            offset: 0,
            limit,
            companyId: company.id,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            setAllDataLoaded((data.recommendingUsers?.results?.length ?? 0) >= (data.recommendingUsers?.total ?? 0));
        },
    });

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);

        if (bottom && !allDataLoaded && !loading) {
            try {
                await fetchMoreRecommendingAllUsers({
                    variables: {
                        offset: recommendingUsersAllData?.recommendingUsers?.results?.length,
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    const recommendations = recommendingUsersAllData?.recommendingUsers?.results;

    return (
        <StyledBox $allowFetchMore={allowFetchMore} onScroll={allowFetchMore ? handleScroll : undefined}>
            {Boolean(recommendations) && (
                <Typography
                    marginLeft="0.5rem"
                    marginBottom="0.5rem"
                    variant="h5"
                    data-target-id="direct-recommendations-text"
                >
                    {t('profile:recommendationsAttribuedToCompany', {
                        count: company.recommendationsReceivedCount,
                        plurial: company.recommendationsReceivedCount > 1 ? 's' : '',
                        companyName: company.name,
                    })}
                </Typography>
            )}
            {recommendations?.map((recommendingUser, index) => (
                <div key={recommendingUser?.id}>
                    <SimpleHorizontalUserCard
                        user={recommendingUser as User}
                        secondSubtitle={
                            recommendingUser?.id === currentUser?.id
                                ? undefined // send undefined to not display common relations for current user
                                : 'commonRelationsCount'
                        }
                        minHeight="5rem"
                        padding="0.3rem 0.75rem"
                        dataTargetId={`recommending-user-card-${recommendingUser?.id}`}
                        backgroundColor="transparent"
                    />

                    {index < recommendations.length - 1 && <Divider />}
                </div>
            ))}
            {loading ? <Loader /> : null}
            {!allowFetchMore && company.recommendationsReceivedCount > limit && (
                <Box display="flex" justifyContent="center" mt="1rem" data-target-id="direct-recommendations-see-all">
                    <Button variant="outlined" onClick={onSeeAllClick}>
                        {t('profile:seeAll')}
                    </Button>
                </Box>
            )}
        </StyledBox>
    );
};

export default DirectRecommendations;
